import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { brokerApi, Broker, BrokerCreateData, BrokerUpdateData } from '../services/api';
import { useTranslation } from 'react-i18next';

interface BrokerFormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  company_name: string;
  commission_rate: number;
  notes: string;
  is_active: boolean;
}

const BrokerManagement: React.FC = () => {
  const { t } = useTranslation();
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBroker, setEditingBroker] = useState<Broker | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Broker | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BrokerFormData>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      address: '',
      company_name: '',
      commission_rate: 0,
      notes: '',
      is_active: true,
    },
  });

  const loadBrokers = async () => {
    try {
      setLoading(true);
      const response = await brokerApi.getAll();
      setBrokers(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load brokers');
      console.error('Error loading brokers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBrokers();
  }, []);

  const handleOpenDialog = (broker?: Broker) => {
    if (broker) {
      setEditingBroker(broker);
      reset({
        name: broker.name,
        email: broker.email || '',
        phone: broker.phone || '',
        address: broker.address || '',
        company_name: broker.company_name || '',
        commission_rate: broker.commission_rate,
        notes: broker.notes || '',
        is_active: broker.is_active,
      });
    } else {
      setEditingBroker(null);
      reset({
        name: '',
        email: '',
        phone: '',
        address: '',
        company_name: '',
        commission_rate: 0,
        notes: '',
        is_active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBroker(null);
    reset();
  };

  const onSubmit = async (data: BrokerFormData) => {
    try {
      if (editingBroker) {
        await brokerApi.update(editingBroker.id, data);
      } else {
        await brokerApi.create(data);
      }
      handleCloseDialog();
      loadBrokers();
    } catch (err) {
      setError('Failed to save broker');
      console.error('Error saving broker:', err);
    }
  };

  const handleDelete = async (broker: Broker) => {
    try {
      await brokerApi.delete(broker.id);
      setDeleteConfirm(null);
      loadBrokers();
    } catch (err) {
      setError('Failed to delete broker');
      console.error('Error deleting broker:', err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          {t('Broker Management')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('Add Broker')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('Name')}</TableCell>
              <TableCell>{t('Company')}</TableCell>
              <TableCell>{t('Contact')}</TableCell>
              <TableCell>{t('Commission Rate')}</TableCell>
              <TableCell>{t('Account Number')}</TableCell>
              <TableCell>{t('Status')}</TableCell>
              <TableCell>{t('Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {brokers.map((broker) => (
              <TableRow key={broker.id}>
                <TableCell>
                  <Typography variant="body1" fontWeight="medium">
                    {broker.name}
                  </Typography>
                  {broker.address && (
                    <Typography variant="body2" color="textSecondary">
                      {broker.address}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {broker.company_name || '-'}
                </TableCell>
                <TableCell>
                  <Box>
                    {broker.email && (
                      <Typography variant="body2">
                        {broker.email}
                      </Typography>
                    )}
                    {broker.phone && (
                      <Typography variant="body2">
                        {broker.phone}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${broker.commission_rate}%`}
                    color="primary"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {broker.account_number || '-'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={broker.is_active ? t('Active') : t('Inactive')}
                    color={broker.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={t('Edit')}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(broker)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('Delete')}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteConfirm(broker)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingBroker ? t('Edit Broker') : t('Add New Broker')}
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Controller
                name="name"
                control={control}
                rules={{ required: t('Name is required') }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('Name')}
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />

              <Controller
                name="company_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('Company Name')}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="email"
                control={control}
                rules={{
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('Invalid email address'),
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('Email')}
                    type="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />

              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('Phone')}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="commission_rate"
                control={control}
                rules={{
                  min: { value: 0, message: t('Commission rate must be 0 or greater') },
                  max: { value: 100, message: t('Commission rate cannot exceed 100%') },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('Commission Rate (%)')}
                    type="number"
                    fullWidth
                    error={!!errors.commission_rate}
                    helperText={errors.commission_rate?.message}
                  />
                )}
              />

              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                      />
                    }
                    label={t('Active')}
                  />
                )}
              />

              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('Address')}
                    fullWidth
                    multiline
                    rows={2}
                  />
                )}
              />

              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('Notes')}
                    fullWidth
                    multiline
                    rows={3}
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              {t('Cancel')}
            </Button>
            <Button type="submit" variant="contained">
              {editingBroker ? t('Update') : t('Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>{t('Confirm Delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('Are you sure you want to delete broker')} "{deleteConfirm?.name}"?
            {t('This action cannot be undone.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            color="error"
            variant="contained"
          >
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrokerManagement; 